import { useEffect, useMemo, useState } from 'react';
import { useApi } from '@backstage/frontend-plugin-api';
import {
  catalogApiRef,
  type CatalogApi,
} from '@backstage/plugin-catalog-react';
import type { CreateWizardContribution } from '../appModules';
import { useAppTypeModules } from '../appModules';
import { creatorOf } from '../appProjectForm/creationAppType';
import {
  CREATE_WIZARD_SUBTITLE,
  createWizardTitle,
} from '../projectWizard/wizardTitle';
import {
  DESCRIPTORS_WAIT_MS,
  useAppTypeDescriptors,
} from '../shared/templateRoles';
import { isWizardTemplatePath, templateRefOfPath } from './subPages';
import { useCreateWizards } from './useCreateWizards';

/**
 * The annotation of a Template entity by which it opts in to the core's
 * generic creation wizard (`GenericProjectWizard`), and its value: the
 * template's form must be written for it (`AppCapacityPicker`,
 * `AppBranchesPicker`, Wagtail v2 plan, step 5). Opt-in rather than
 * automatic: a creation template opens the generic wizard only once it is
 * migrated to those fields, so neither repository (this one,
 * platform-catalog) has to ship first; without the annotation, a template
 * in plain JSON Schema keeps upstream's form.
 */
export const WIZARD_ANNOTATION = 'waadoo.ovh/wizard';
export const GENERIC_WIZARD = 'generic';

/** What « Créer » shows for one template's path. */
export type TemplateWizard =
  /** Upstream's form (or no template at all): the page as it was. */
  | { kind: 'standard' }
  /** Not known yet: neither form, so that none flashes. */
  | { kind: 'pending' }
  /** A type's module brings its own wizard (`createWizard`, Odoo's). */
  | { kind: 'module'; wizard: CreateWizardContribution }
  /** The core's generic wizard, for an opted-in template. */
  | { kind: 'generic'; templateRef: string; title: string; subtitle: string };

/** As the descriptors' cache (`templateRoles.ts`, `DESCRIPTOR_TTL_MS`). */
const OPT_IN_TTL_MS = 30_000;

type OptInEntry = { at: number; promise: Promise<boolean> };

/**
 * How long the annotation's read is waited for before it answers « no
 * opt-in » — upstream's form, as without the annotation —, so that no
 * template's form waits on a request that does not end. The same wait as
 * the descriptors' (`DESCRIPTORS_WAIT_MS`), the page's one tolerance for a
 * slow backend: shorter, a slow but working catalog would turn an opted-in
 * template's wizard into upstream's form more often. A later answer is
 * ignored, so that the form shown does not change under the reader's eyes.
 */
export const OPT_IN_WAIT_MS = DESCRIPTORS_WAIT_MS;

/**
 * One catalog read per template every 30 s, off the app's `CatalogApi`
 * instance: the page's header (`CreatePage`) and its body
 * (`TemplatesSubPage`) ask at the same time, and share the answer. A
 * rejected promise is dropped, and so is one that gave up
 * (`OPT_IN_WAIT_MS`).
 */
const optIns = new WeakMap<CatalogApi, Map<string, OptInEntry>>();

/** Whether the Template `templateRef` carries `waadoo.ovh/wizard: generic`. */
export function loadWizardOptIn(
  catalogApi: CatalogApi,
  templateRef: string,
): Promise<boolean> {
  const byRef = optIns.get(catalogApi) ?? new Map<string, OptInEntry>();
  optIns.set(catalogApi, byRef);
  const now = Date.now();
  const cached = byRef.get(templateRef);
  if (cached && now - cached.at < OPT_IN_TTL_MS) return cached.promise;
  const read = catalogApi
    .getEntitiesByRefs({
      entityRefs: [templateRef],
      // `metadata.name` too, so that `metadata` is there without annotations.
      fields: ['metadata.name', 'metadata.annotations'],
    })
    .then(
      ({ items: [entity] }) =>
        entity?.metadata.annotations?.[WIZARD_ANNOTATION] === GENERIC_WIZARD,
    );
  let timer: ReturnType<typeof setTimeout> | undefined;
  const givenUp = new Promise<boolean>(resolve => {
    timer = setTimeout(() => resolve(false), OPT_IN_WAIT_MS);
  });
  const promise = Promise.race([read, givenUp]).finally(() =>
    clearTimeout(timer),
  );
  const entry: OptInEntry = { at: now, promise };
  byRef.set(templateRef, entry);
  // Given up: not kept, the next opening reads again. (Never settles when
  // the read answered first: its timer is cleared.)
  void givenUp.then(() => {
    if (byRef.get(templateRef) === entry) byRef.delete(templateRef);
  });
  // The caller handles the rejection; this branch only forgets it.
  void promise.catch(() => {
    if (byRef.get(templateRef) === entry) byRef.delete(templateRef);
  });
  return promise;
}

/**
 * Whether `templateRef` opts in (`loadWizardOptIn`); undefined while it is
 * read, never the previous template's answer. A failed read, or one that
 * gave up (`OPT_IN_WAIT_MS`), is no opt-in: upstream's form, as before (it
 * reports a failure itself).
 */
function useWizardOptIn(templateRef: string | undefined): boolean | undefined {
  const catalogApi = useApi(catalogApiRef);
  const [answer, setAnswer] = useState<{ ref: string; generic: boolean }>();

  useEffect(() => {
    if (templateRef === undefined) return undefined;
    let active = true;
    loadWizardOptIn(catalogApi, templateRef).then(
      generic => {
        if (active) setAnswer({ ref: templateRef, generic });
      },
      () => {
        if (active) setAnswer({ ref: templateRef, generic: false });
      },
    );
    return () => {
      active = false;
    };
  }, [catalogApi, templateRef]);

  if (templateRef === undefined) return false;
  return answer?.ref === templateRef ? answer.generic : undefined;
}

const STANDARD: TemplateWizard = { kind: 'standard' };
const PENDING: TemplateWizard = { kind: 'pending' };

/**
 * Which wizard « Créer » opens for the template path `path` under
 * « templates/ » (`default/wagtail-project`; undefined: not a template's
 * form), in this order (Wagtail v2 plan, step 4, 2026-10-25):
 *  1. a module's own wizard for it (`createWizard`, Odoo's): at once, no
 *     read — Odoo's wizard opens as before;
 *  2. else, when the Template carries `waadoo.ovh/wizard: generic` and the
 *     `AppType` whose `spec.templates.create` it is has no module wizard,
 *     the generic wizard, under « Nouveau projet <type title> »;
 *  3. else upstream's form: no annotation, no type designating it, a type
 *     with a wizard of its own, or a read that failed (the catalog — given
 *     up after `OPT_IN_WAIT_MS` —, the descriptors — `useAppTypeDescriptors`
 *     gives up after `DESCRIPTORS_WAIT_MS`).
 * `pending` while the annotation, then the descriptors, are read.
 */
export function useTemplateWizard(path: string | undefined): TemplateWizard {
  const modules = useAppTypeModules();
  const moduleWizard = useCreateWizards().find(
    item => path !== undefined && isWizardTemplatePath(path, item.templatePath),
  );
  const templateRef =
    path === undefined || moduleWizard ? undefined : templateRefOfPath(path);
  const optIn = useWizardOptIn(templateRef);
  const descriptors = useAppTypeDescriptors({ enabled: optIn === true });

  return useMemo((): TemplateWizard => {
    if (moduleWizard) return { kind: 'module', wizard: moduleWizard };
    if (templateRef === undefined || optIn === false) return STANDARD;
    if (optIn === undefined) return PENDING;
    switch (descriptors.status) {
      case 'ready': {
        const creator = creatorOf(descriptors.items, templateRef);
        if (!creator) return STANDARD;
        // The type's module, as the registry's `get` finds it (the first).
        const owner = modules.find(module => module.appType === creator.name);
        if (owner?.createWizard) return STANDARD;
        return {
          kind: 'generic',
          templateRef,
          title: createWizardTitle(creator.title),
          subtitle: CREATE_WIZARD_SUBTITLE,
        };
      }
      case 'failed':
        return STANDARD;
      default:
        return PENDING;
    }
  }, [moduleWizard, templateRef, optIn, descriptors, modules]);
}
